import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api/api";
import { useCart } from "../context/CartContext";
import "./ProductDetail.css";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [selectedQty, setSelectedQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState("");
  const [imgIndex, setImgIndex] = useState(0);

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const [guestPopup, setGuestPopup] = useState(false);

  useEffect(() => {
    API.get("/products/").then(res => {
      const found = (res.data.results || res.data).find(
        p => String(p.id) === String(id)
      );
      setProduct(found || null);
      if (found?.sizes?.length > 0) setSelectedSize(found.sizes[0]);
    });
  }, [id]);

  if (!product) return <div className="loading">Loading...</div>;

  const stock = product.quantity;
  const API_BASE = process.env.REACT_APP_API_BASE ;
  const images =
    Array.isArray(product.product_images)
      ? product.product_images.map(i => {
          const imgUrl = i.image || i;
          if (typeof imgUrl === "string" && !imgUrl.startsWith("http")) {
            return `${API_BASE}${imgUrl}`;
          }
          return imgUrl;
        }).filter(Boolean)
      : [];
  const hasImages = images.length > 0;
  const image = hasImages
    ? images[imgIndex]
    : "https://via.placeholder.com/500x500?text=No+Image";

  function cycleImg(delta) {
    if (!hasImages) return;
    setImgIndex(i => (i + delta + images.length) % images.length);
  }

  function handleSwipe(e) {
    const t = e.touches[0].clientX;
    e.target._swipeLast = t;
  }

  function handleSwipeEnd(e) {
    if (!hasImages || !e.target._swipeLast) return;
    const diff = e.changedTouches[0].clientX - e.target._swipeLast;
    if (Math.abs(diff) > 30) cycleImg(diff > 0 ? -1 : 1);
  }

  function showMessage(message, type = "success") {
    setPopupMessage(message);
    setPopupType(type);
    setShowPopup(true);
    setTimeout(() => setShowPopup(false), 2200);
  }

  function handleAddToCart() {
    const token = localStorage.getItem("access");

    if (!token) {
      // Guest popup with OK/Cancel
      setGuestPopup(true);
      return;
    }

    if (stock === 0) {
      showMessage("Product is out of stock", "error");
      return;
    }

    addToCart(product.id, selectedQty, product.color, selectedSize)
      .then(() => showMessage("Added to cart successfully", "success"))
      .catch(err =>
        showMessage(err.message || "Could not add product to cart", "error")
      );
  }

  function handleGuestOk() {
    setGuestPopup(false);
    navigate("/login");
  }

  function handleGuestCancel() {
    setGuestPopup(false);
  }

  return (
    <div className="product-page">
      <div className="product-container">
        {/* IMAGE AND BACK BUTTON */}
        <div className="product-image-section">
          <button
            className="back-btn"
            onClick={() => navigate("/")}
            type="button"
          >
            ← Back
          </button>
          <div
            className="image-carousel"
            onTouchStart={handleSwipe}
            onTouchEnd={handleSwipeEnd}
            tabIndex={0}
            aria-label="Product images"
          >
            <img src={image} alt={product.name} className="product-image" />
            {hasImages && images.length > 1 && (
              <>
                <button
                  className="carousel-btn prev"
                  onClick={() => cycleImg(-1)}
                  aria-label="Previous Image"
                  tabIndex={0}
                >
                  &#8249;
                </button>
                <button
                  className="carousel-btn next"
                  onClick={() => cycleImg(1)}
                  aria-label="Next Image"
                  tabIndex={0}
                >
                  &#8250;
                </button>
                <div className="carousel-dots">
                  {images.map((_, i) => (
                    <span
                      key={i}
                      className={"dot" + (i === imgIndex ? " active" : "")}
                      onClick={() => setImgIndex(i)}
                    />
                  ))}
                </div>
              </>
            )}
            {stock === 0 && <div className="stock-badge">OUT OF STOCK</div>}
          </div>
        </div>
        {/* PRODUCT INFO */}
        <div className="product-info">
          <h1 className="product-name">{product.name}</h1>
          <div className="product-price">ETB {product.price}</div>
          <div className="product-stock">Available: {stock}</div>
          {Array.isArray(product.sizes) && product.sizes.length > 0 && (
            <div className="input-group">
              <label>Size</label>
              <select
                value={selectedSize}
                onChange={e => setSelectedSize(e.target.value)}
              >
                {product.sizes.map(size => (
                  <option key={size}>{size}</option>
                ))}
              </select>
            </div>
          )}
          <div className="input-group">
            <label>Quantity</label>
            <div className="qty-row">
              <button
                className="qty-btn"
                aria-label="Decrease quantity"
                disabled={selectedQty <= 1}
                onClick={() => setSelectedQty(qty => Math.max(1, qty - 1))}
              >
                -
              </button>
              <input
                type="number"
                min="1"
                max={stock}
                value={selectedQty}
                onChange={e =>
                  setSelectedQty(
                    Math.max(1, Math.min(stock, Number(e.target.value)))
                  )
                }
              />
              <button
                className="qty-btn"
                aria-label="Increase quantity"
                disabled={selectedQty >= stock}
                onClick={() =>
                  setSelectedQty(qty => Math.min(stock, qty + 1))
                }
              >
                +
              </button>
            </div>
          </div>
          <button
            className="add-cart-btn"
            disabled={stock === 0}
            onClick={handleAddToCart}
          >
            Add To Cart
          </button>
        </div>
      </div>

      {/* Normal popup for authorized users */}
      {showPopup && (
        <div className="popup-overlay">
          <div className={`popup-box ${popupType}`}>
            <div className="popup-icon">
              {popupType === "success" ? "✓" : "✕"}
            </div>
            <div className="popup-text">{popupMessage}</div>
          </div>
        </div>
      )}

      {/* Guest popup with OK / Cancel */}
      {guestPopup && (
  <div className="popup-overlay">
    <div className="popup-box guest-popup">
      <div className="popup-header">Login Required</div>
      <div className="popup-text">
        You need to login first to order this product.
      </div>
      <div className="popup-buttons">
        <button className="ok-btn" onClick={handleGuestOk}>OK</button>
        <button className="cancel-btn" onClick={handleGuestCancel}>Cancel</button>
      </div>
    </div>
  </div>
)}
    </div>
  );
}